/**
 * StatusDropdown — Custom dropdown that replaces static status pills.
 * Shows valid status transitions for a sub-session, including "Next" (promote to ready).
 * Terminal statuses (completed, cancelled) render as read-only pills.
 * Uses getStatusBadgeClass from statusColors.ts for consistent styling.
 */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { getStatusBadgeClass } from '../utils/statusColors';
import { useLanguage } from '../i18n/LanguageContext';

interface StatusDropdownProps {
  currentStatus: string;
  subSessionNumber: string;
  hasStation: boolean;
  hasOperator: boolean;
  onStatusChange: (subSessionNumber: string, newStatus: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

/** Map of current status to available transition options */
const getTransitionOptions = (
  currentStatus: string,
  hasStation: boolean,
  hasOperator: boolean,
  t: (key: string) => string
): { value: string; label: string }[] => {
  switch (currentStatus) {
    case 'received':
      return [
        // "Next" is a special action that force-promotes via promote-next endpoint
        ...(hasStation && hasOperator
          ? [{ value: 'next', label: t('sessionDetails.statusDropdown.next') }]
          : []),
        ...(hasStation && hasOperator
          ? [{ value: 'ready', label: t('sessionDetails.statusDropdown.promoteToReady') }]
          : []),
        { value: 'cancelled', label: t('sessionDetails.cancelSession') },
      ];
    case 'ready':
      return [
        { value: 'received', label: t('sessionDetails.statusDropdown.demoteToPlanned') },
        { value: 'cancelled', label: t('sessionDetails.cancelSession') },
      ];
    case 'active':
      return [
        { value: 'paused', label: t('sessionDetails.pauseSession') },
        { value: 'completed', label: t('sessionDetails.completeSession') },
      ];
    case 'paused':
      return [
        { value: 'active', label: t('sessionDetails.resumeSession') },
        { value: 'completed', label: t('sessionDetails.completeSession') },
        { value: 'cancelled', label: t('sessionDetails.cancelSession') },
      ];
    default:
      return [];
  }
};

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  currentStatus,
  subSessionNumber,
  hasStation,
  hasOperator,
  onStatusChange,
  disabled = false,
  size = 'sm',
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isTerminal = ['completed', 'cancelled'].includes(currentStatus);
  const options = getTransitionOptions(currentStatus, hasStation, hasOperator, t);

  // Pill sizing
  const pillClasses = size === 'sm'
    ? 'px-2 py-0.5 text-xs'
    : 'px-3 py-1 text-sm';

  // Terminal statuses render as read-only pills
  if (isTerminal || disabled || options.length === 0) {
    return (
      <span className={`inline-flex ${pillClasses} font-medium rounded-full ${getStatusBadgeClass(currentStatus)}`}>
        {t(`status.${currentStatus}`) || currentStatus}
      </span>
    );
  }

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Trigger button styled like a status pill */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`inline-flex items-center gap-1 ${pillClasses} font-medium rounded-full cursor-pointer hover:opacity-80 transition-opacity ${getStatusBadgeClass(currentStatus)}`}
      >
        {t(`status.${currentStatus}`) || currentStatus}
        <ChevronDown className={`${size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 right-0 min-w-[200px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg py-1">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
                onStatusChange(subSessionNumber, option.value);
              }}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {option.value !== 'next' && option.value !== 'cancelled' && (
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${getStatusBadgeClass(option.value).split(' ')[0]}`} />
              )}
              {option.value === 'next' && (
                <span className="inline-block w-2 h-2 rounded-full mr-2 bg-cyan-500" />
              )}
              {option.value === 'cancelled' && (
                <span className="inline-block w-2 h-2 rounded-full mr-2 bg-red-500" />
              )}
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusDropdown;
